import { ANGLES_D_OUVERTURE, COULEURS_DE_BULLE, FONDS_DE_PROJET, FORMES_DE_BULLE, FORMES_DE_PROJET, TRAITS_DE_PROJET, } from './listes.js';
/** Un projet qui n'a rien réglé : Uni, Galet, Crayon (décision d'Igor du 2026-10-20), ouvert en Carte. */
export const HABIT_PAR_DEFAUT = Object.freeze({
    fond: 'uni',
    forme: 'galet',
    trait: 'crayon',
    angle: 'carte',
});
function parmi(valeur, liste, defaut) {
    return liste.includes(valeur ?? '') ? valeur : defaut;
}
/**
 * L'habit d'un projet tel que la base le garde : `null` vaut le défaut, et une valeur hors de sa
 * liste aussi (l'écran ne reçoit jamais une forme qu'il ne sait pas dessiner). Aucune migration
 * ne transforme les projets déjà là : ils portent l'habit par défaut.
 */
export function habitDuProjet(ligne) {
    return {
        fond: parmi(ligne.fond, FONDS_DE_PROJET, HABIT_PAR_DEFAUT.fond),
        forme: parmi(ligne.forme, FORMES_DE_PROJET, HABIT_PAR_DEFAUT.forme),
        trait: parmi(ligne.trait, TRAITS_DE_PROJET, HABIT_PAR_DEFAUT.trait),
        angle: parmi(ligne.angle, ANGLES_D_OUVERTURE, HABIT_PAR_DEFAUT.angle),
    };
}
/*
 * L'habit d'une bulle, par-dessus celui de son projet (chantier habit-des-bulles, modification
 * 01) : sa couleur, sa forme et son trait à elle ; `null` en base vaut « comme le projet ». Seule
 * la couleur suit la mère (décision d'Igor du 2026-10-21) ; la forme et le trait restent bulle par
 * bulle.
 */
/** La valeur si elle est dans sa liste, sinon `null` : une valeur inconnue vaut « rien de choisi ». */
function choisie(valeur, liste) {
    return liste.includes(valeur ?? '') ? valeur : null;
}
/**
 * **La couleur d'une branche**, la règle écrite une fois : celle de la bulle, sinon celle de la
 * mère la plus proche qui en a une, sinon `null` (l'axe automatique). Le projet ne donne pas la
 * sienne : sa couleur est celle du dossier, pas d'une branche. Le serveur l'appelle (la feuille,
 * les intercalaires), l'écran aussi (la Carte, la Hiérarchie) : jamais une seconde copie.
 *
 * `lire` rend une bulle par son identifiant ; `auDessus`, la couleur qui vient des mères qu'on ne
 * lit pas (l'écran ne connaît que la feuille : il passe la `couleurHeritee` de sa bulle). Une
 * couleur hors de la liste ne compte pas ; une boucle sur les mères s'arrête.
 */
export function couleurDeBranche(id, lire, auDessus = null) {
    const vues = new Set();
    let bulle = lire(id);
    while (bulle && !vues.has(bulle.id)) {
        vues.add(bulle.id);
        if (bulle.parentId === null)
            return null;
        const sienne = choisie(bulle.couleur, COULEURS_DE_BULLE);
        if (sienne)
            return sienne;
        const mere = lire(bulle.parentId);
        if (!mere)
            return auDessus;
        bulle = mere;
    }
    return null;
}
/**
 * L'habit d'une bulle pour la feuille : une valeur hors de sa liste vaut `null`. Sur le projet,
 * la forme et le trait sont ceux du projet (`habitDuProjet`), pas les siens : `null` ici ; sa
 * couleur reste celle du dossier.
 */
export function habitDeLaBulle(ligne) {
    const projet = ligne.parentId === null;
    return {
        couleur: choisie(ligne.couleur, COULEURS_DE_BULLE),
        forme: projet ? null : choisie(ligne.forme, FORMES_DE_BULLE),
        trait: projet ? null : choisie(ligne.trait, TRAITS_DE_PROJET),
    };
}
